import AppContext from "@/AppContext";
import videoCover from "@/assets/images/intro-video-cover.jpg";
import contentBenefits2 from "@/assets/json/content-benefits-2.json";
import freeBenefits2 from "@/assets/json/free-benefits-2.json";
import investorBenefits2 from "@/assets/json/investor-benefits-2.json";
import plans from "@/assets/json/plans.json";
import startupBenefits2 from "@/assets/json/startup-benefits-2.json";
import Layout from "@/components/elements/Layout";
import SubNavigationMobile from "@/components/elements/SubNavigationMobile";
import PlanCard from "@/components/pricing/PlanCard";
import MobilePricingComparison from "@/components/pricing/MobilePricingComparison";
import availablePricesDev from "@/hooks/availablePrices.dev";
import availablePricesMonthlyDev from "@/hooks/availablePrices.monthly.dev";
import availablePricesMonthlyProd from "@/hooks/availablePrices.monthly.prod";
import availablePricesProd from "@/hooks/availablePrices.prod";
import { Play } from "@phosphor-icons/react";
import axios from "axios";
import Image from "next/legacy/image";
import { useContext, useState } from "react";
import ReactPlayer from "react-player/lazy";

const Pricing = ({
  userLocation,
  priceData: _priceData,
  priceDataMonthly,
  priceIds,
  priceIdsMonthly,
  currencyCode,
  ip,
}) => {
  const context = useContext(AppContext);
  const { planSubscription, loading } = context.state;
  const [playing, setIsPlaying] = useState(false);
  const [billingCycle, setBillingCycle] = useState("yearly");
  // Get the appropriate price data for Content Access plan only
  // Other plans (Startup, VC, Angel, Free) always use yearly pricing
  const priceData = {
    ..._priceData,
    "Content Access":
      billingCycle === "monthly"
        ? priceDataMonthly["Content Access"]
        : _priceData["Content Access"],
  };

  const handlePlayPause = () => {
    setIsPlaying(!playing);
  };

  return (
    <Layout
      title={"Plans | Lucidity Insights"}
      description={
        "Find the perfect pricing plan at Lucidity Insights. From free registration to comprehensive startup access, we provide essential data"
      }
    >
      <div className='container lg:hidden'>
        <SubNavigationMobile label='Pricing' />
      </div>
      <div className='pt-4 lg:overflow-hidden lg:py-16 bg-background'>
        <div className='container flex flex-col items-center gap-6 max-w-[1560px] lg:flex-row lg:gap-16'>
          <div className='relative lg:w-[45%]'>
            <h2 className='max-w-5xl mx-auto text-[28px] font-bold tracking-tight text-white leading-[1.1] lg:text-6xl'>
              Discover the Plan That{" "}
              <span className='inline-block text-transparent bg-clip-text bg-gradient-to-tr from-yellow-1 to-red-1'>
                Works
              </span>{" "}
              for You...
            </h2>
            <p className='max-w-4xl mx-auto mt-3 text-sm lg:mt-4 lg:text-base text-white/60'>
              We believe in the democratization of top tier data and insights
              for emerging tech ecosystems, but we do ask for a small monthly
              contribution for regular users that feel they benefit from our
              work.
            </p>
          </div>

          <div className='relative hidden lg:block lg:flex-1'>
            <div className='w-full p-1 rounded-2xl bg-gradient-to-tr from-yellow-1 to-red-1'>
              <div className='relative overflow-hidden rounded-xl'>
                {!playing && (
                  <button
                    onClick={handlePlayPause}
                    className={`absolute z-10 top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 inline-flex items-center justify-center w-16 lg:w-20 h-16 lg:h-20 rounded-full border-background bg-gradient-to-tr from-yellow-1 to-red-1 ${
                      playing ? "" : "pulse"
                    }`}
                  >
                    <Play weight='fill' className='text-2xl lg:text-3xl' />
                  </button>
                )}
                {!playing && (
                  <Image
                    src={videoCover}
                    objectFit='cover'
                    layout='fill'
                    alt=''
                  />
                )}
                <ReactPlayer
                  url={
                    "https://cdn.lucidityinsights.com/uploads/lucidity/adv2025.mp4"
                  }
                  width='100%'
                  height='100%'
                  playing={playing}
                  controls={playing}
                  stopOnUnmount={false}
                  pip
                />
              </div>
            </div>
          </div>
        </div>

        {/* Mobile comparison — hidden on desktop via CSS */}
        <div className='lg:hidden'>
          <MobilePricingComparison
            planSubscription={planSubscription}
            currencyCode={currencyCode}
            priceData={priceData}
            priceDataMonthly={priceDataMonthly}
            billingCycle={billingCycle}
            setBillingCycle={setBillingCycle}
            _priceData={_priceData}
            planConfigs={[
              {
                plan: plans.find((plan) => plan.plan_id === "Free"),
                benefits: freeBenefits2,
                description:
                  "Access insights, news, and expert content. A great starting point to grow your venture.",
              },
              {
                plan: plans.find((plan) => plan.plan_id === "Content Access"),
                benefits: contentBenefits2,
                description:
                  "Full access to reports, insights, and trends. Perfect for deep analysis and informed decisions.",
              },
              {
                plan: plans.find(
                  (plan) => plan.plan_id === "Startup Registration",
                ),
                benefits: startupBenefits2,
                description:
                  "Showcase your startup to active investors. Get discovered and start meaningful conversations.",
              },
              {
                plan: plans.find((plan) => plan.plan_id === "VC Registration"),
                benefits: investorBenefits2,
                description:
                  "Discover promising startups and founders. Ideal for sourcing opportunities and deal flow.",
              },
            ]}
          />
        </div>

        {/* Desktop grid — hidden on mobile via CSS */}
        <div className='container items-start hidden grid-cols-4 gap-4 mt-16 lg:grid'>
          <PlanCard
            planSubscription={planSubscription}
            currencyCode={currencyCode}
            priceData={priceData}
            plan={plans.find((plan) => plan.plan_id === "Free")}
            benefits={freeBenefits2}
            description={
              "Access insights, news, and expert content. A great starting point to grow your venture."
            }
          />
          <PlanCard
            planSubscription={planSubscription}
            currencyCode={currencyCode}
            priceData={priceData}
            priceDataMonthly={priceDataMonthly}
            setBillingCycle={setBillingCycle}
            billingCycle={billingCycle}
            _priceData={_priceData}
            plan={plans.find((plan) => plan.plan_id === "Content Access")}
            benefits={contentBenefits2}
            description={
              "Full access to reports, insights, and trends. Perfect for deep analysis and informed decisions."
            }
            highlighted
          />
          <PlanCard
            planSubscription={planSubscription}
            currencyCode={currencyCode}
            priceData={priceData}
            plan={plans.find((plan) => plan.plan_id === "Startup Registration")}
            description={
              "Showcase your startup to active investors. Get discovered and start meaningful conversations."
            }
            benefits={startupBenefits2}
          />
          <PlanCard
            planSubscription={planSubscription}
            currencyCode={currencyCode}
            priceData={priceData}
            plan={plans.find((plan) => plan.plan_id === "VC Registration")}
            description={
              "Discover promising startups and founders. Ideal for sourcing opportunities and deal flow."
            }
            benefits={investorBenefits2}
            hasVCAngelSelection={true}
          />
        </div>
        {/* {!isMobile && (
          <Tabs defaultValue='startup' className='container max-w-[1560px]'>
            <div className='flex flex-col items-center py-8 lg:py-16'>
              <p className='mb-3 text-3xl font-semibold tracking-tight text-center lg:mb-6 lg:text-4xl'>
                First, how would you describe yourself?
              </p>
              <TabsList className='grid h-auto rounded-full w-full text-lg grid-cols-3 max-w-[440px]'>
                <TabsTrigger className='price-tabs' value='data-junkies'>
                  Data Junkies
                </TabsTrigger>
                <TabsTrigger className='price-tabs' value='startup'>
                  Startup Founder
                </TabsTrigger>
                <TabsTrigger className='price-tabs' value='investor'>
                  Investor
                </TabsTrigger>
              </TabsList>
              <p className='mt-3 text-xs opacity-40'>
                Choose one that suites you best
              </p>
            </div>
            <TabsContent value='data-junkies'>
              <div className='grid items-start gap-4 lg:grid-cols-2'>
                <PlanCard
                  planSubscription={planSubscription}
                  currencyCode={currencyCode}
                  priceData={priceData}
                  plan={plans.find((plan) => plan.plan_id === "Free")}
                  benefits={freeBenefits}
                />
                <PlanCard
                  planSubscription={planSubscription}
                  currencyCode={currencyCode}
                  priceData={priceData}
                  priceDataMonthly={priceDataMonthly}
                  setBillingCycle={setBillingCycle}
                  billingCycle={billingCycle}
                  _priceData={_priceData}
                  plan={plans.find((plan) => plan.plan_id === "Content Access")}
                  benefits={contentBenefits}
                  highlighted
                />
              </div>
            </TabsContent>
            <TabsContent value='startup'>
              <div className='grid items-start gap-4 lg:grid-cols-3'>
                <PlanCard
                  planSubscription={planSubscription}
                  currencyCode={currencyCode}
                  priceData={priceData}
                  plan={plans.find((plan) => plan.plan_id === "Free")}
                  benefits={freeBenefits}
                />
                <PlanCard
                  planSubscription={planSubscription}
                  currencyCode={currencyCode}
                  priceData={priceData}
                  priceDataMonthly={priceDataMonthly}
                  setBillingCycle={setBillingCycle}
                  billingCycle={billingCycle}
                  _priceData={_priceData}
                  plan={plans.find((plan) => plan.plan_id === "Content Access")}
                  benefits={contentBenefits}
                />
                <PlanCard
                  planSubscription={planSubscription}
                  currencyCode={currencyCode}
                  priceData={priceData}
                  plan={plans.find(
                    (plan) => plan.plan_id === "Startup Registration"
                  )}
                  benefits={startupBenefits}
                  highlighted
                />
              </div>
            </TabsContent>
            <TabsContent value='investor'>
              <div className='grid items-start gap-4 lg:grid-cols-3'>
                <PlanCard
                  planSubscription={planSubscription}
                  currencyCode={currencyCode}
                  priceData={priceData}
                  plan={plans.find((plan) => plan.plan_id === "Free")}
                  benefits={freeBenefits}
                />
                <PlanCard
                  planSubscription={planSubscription}
                  currencyCode={currencyCode}
                  priceData={priceData}
                  plan={plans.find(
                    (plan) => plan.plan_id === "VC Registration"
                  )}
                  benefits={investorBenefits}
                  highlighted
                  hideHighligtedGraphic
                />
                <PlanCard
                  planSubscription={planSubscription}
                  currencyCode={currencyCode}
                  priceData={priceData}
                  plan={plans.find(
                    (plan) => plan.plan_id === "Angel Investor Registration"
                  )}
                  benefits={investorBenefits}
                  highlighted
                  hideHighligtedGraphic
                />
              </div>
            </TabsContent>
          </Tabs>
        )} */}
      </div>
    </Layout>
  );
};

export async function getServerSideProps({ req, query }) {
  // Import geolocation utility dynamically to avoid circular dependencies
  const { getGeolocationData, getUserIpFromRequest } =
    await import("@/utils/geolocation");

  // Get the user's real IP address
  const userIp = getUserIpFromRequest(req);

  // Fetch geolocation data using the shared utility (includes API key)
  const geoData = await getGeolocationData(userIp);

  // Create response object for compatibility with existing code
  const response = geoData.success ? { data: geoData } : null;

  const availablePrices =
    process.env.ENV !== "production" ? availablePricesDev : availablePricesProd;

  const availablePricesMonthly =
    process.env.NEXT_PUBLIC_ENV !== "production"
      ? availablePricesMonthlyDev
      : availablePricesMonthlyProd;

  let priceData = {};
  let priceDataMonthly = {};
  let currencyCode = "USD";
  let priceIds = availablePrices["USD"];
  let priceIdsMonthly = availablePricesMonthly["USD"];
  const codes = ["AED", "IDR", "JOD", "EGP", "SAR"];

  if (response?.data?.success && response?.data?.currency_code) {
    const currencyCodeFromApi = response.data.currency_code;
    if (codes.includes(currencyCodeFromApi)) {
      currencyCode = currencyCodeFromApi;
      priceIds = availablePrices[currencyCode];
      priceIdsMonthly = availablePricesMonthly[currencyCode];
    }
  }

  const stripe = require("stripe")(process.env.STRIPE_SECRET_KEY);

  for (const pKey in priceIds) {
    const stripePriceData = await stripe.prices.retrieve(priceIds[pKey]);
    priceData[pKey] = stripePriceData;
  }

  for (const pKey in priceIdsMonthly) {
    const stripePriceDataMonthly = await stripe.prices.retrieve(
      priceIdsMonthly[pKey],
    );
    priceDataMonthly[pKey] = stripePriceDataMonthly;
  }

  return {
    props: {
      userLocation: response?.data || null,
      currencyCode,
      priceData,
      priceDataMonthly,
      priceIds,
      priceIdsMonthly,
      ip: userIp,
    },
  };
}

export default Pricing;
